import type { DropdownMenuItem } from '@nuxt/ui';
import type { ComputedRef, Reactive } from 'vue';
import type { DetailPageContext } from '#layers/block-engine';
import type { CompositePrimaryKey } from '#layers/data-model';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#app/composables/router';
import { useStore } from '#imports';
import { CrudAction } from '#layers/access';
import { showConfirmationDialog } from '#layers/ui/utils/show-confirmation-dialog';
import { useAuthorization } from '~/composables/use-authorization';
import { useCollectionRoutes } from '~/composables/use-collection-routes';
import { useToastResultHandling } from '~/composables/use-toast-result-handling';

export interface UseFormBlockOverflowActionsParams {
	isCreate: ComputedRef<boolean>;
	collectionData: ComputedRef<ResolvedMonospaceCollection | undefined>;
	parsedFinalItemId: ComputedRef<CompositePrimaryKey | null>;
	detailPageContext: Reactive<DetailPageContext & { isDetailPage: boolean }>;
}

export interface UseFormBlockOverflowActionsReturn {
	overflowActions: ComputedRef<DropdownMenuItem[]>;
}

export function useFormBlockOverflowActions(params: UseFormBlockOverflowActionsParams): UseFormBlockOverflowActionsReturn {
	const { isCreate, collectionData, parsedFinalItemId, detailPageContext } = params;

	const store = useStore();
	const router = useRouter();
	const { t } = useI18n();
	const { can } = useAuthorization();
	const { listingRoute } = useCollectionRoutes(collectionData);
	const { onSuccess: deleteSuccess, onError: deleteError } = useToastResultHandling({
		success: {
			title: t('pages.content.delete_item.success'),
		},
		error: {
			title: t('pages.content.delete_item.error'),
		},
	});

	const canDeleteItem = computed(() =>
		collectionData.value
			? can({ collection: collectionData.value, action: CrudAction.Delete, id: parsedFinalItemId.value ?? undefined })
			: false,
	);

	const overflowActions = computed(() => {
		const actions: DropdownMenuItem[] = [];

		if (!isCreate.value && !detailPageContext.pageDisplayMode && canDeleteItem.value) {
			// only add delete action if the item exists and the page is not shown in a drawer
			// TODO this needs a better indicator. A page might be shown in a modal but we still want to show this
			actions.push({
				label: t('pages.content.delete_item.action'),
				color: 'error',
				onSelect: async () => {
					if (!collectionData.value) return;

					const storeApi = store.$collection(collectionData.value.apiName);
					const key = storeApi.getKey(parsedFinalItemId.value);

					if (!key) return;

					const confirmed = await showConfirmationDialog({
						props: {
							title: t('pages.content.delete_item.title'),
							description: t('pages.content.delete_item.description'),
							confirm: {
								color: 'error',
								label: t('pages.content.delete_item.confirm'),
							},
						},
					});

					if (!confirmed) return;

					try {
						await storeApi?.delete(key);

						deleteSuccess();

						if (listingRoute.value) {
							router.replace(listingRoute.value);
						}
					}
					catch (error) {
						deleteError(error);
					}
				},
			} as const);
		}

		return actions;
	});

	return {
		overflowActions,
	};
}
